import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CookieConsent from "../components/CookieConsent";
// Consent banner lives globally in App.js — do not render here.
const CookieConsentInline = () => null;
import { ArrowRight, Zap, Shield, Clock, CheckCircle, Star, FileText, Users, TrendingUp, Package, Handshake, BarChart3, Lightbulb, Code2, UserCheck, Layers, Flame, Target, AlertTriangle, X } from "lucide-react";

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_mvp-builder-56/artifacts/thsweg98_ChatGPT%20Image%20May%204%2C%202026%2C%2004_46_32%20PM.png";

const HeroSection = ({ onStart }) => (
  <section className="pt-24 pb-16 md:pt-32 md:pb-24 bg-white" data-testid="hero-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-in">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest bg-zinc-100 text-zinc-700 border border-zinc-200 mb-6">
            <Zap size={12} className="mr-1.5" /> For founders shipping before they hire
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] text-zinc-950 mb-4">
            Validate, demo, and pitch your product —<br className="hidden md:block" /> <span className="bg-gradient-to-r from-emerald-600 via-teal-500 to-sky-600 bg-clip-text text-transparent">before you hire a dev team.</span>
          </h1>
          <p data-testid="hero-tagline" className="text-base sm:text-lg text-zinc-700 leading-relaxed mb-5 max-w-xl">
            We build prototypes that <strong className="text-zinc-950">actually survive due diligence</strong> — AI-built, senior-dev validated, production-grade architecture. Live in days, not months.
          </p>
          <p className="text-sm text-zinc-600 leading-relaxed mb-4 max-w-xl">
            Skip the $100K dev team gamble. Test your idea with a real working product, capture user data, and walk into your seed round with proof — not slides.
          </p>
          <p className="text-sm text-blue-700 font-medium bg-blue-50 border border-blue-100 rounded-xl px-4 py-2.5 mb-8 max-w-lg">
            <strong>Speed is your moat.</strong> Launch before your competitors finish writing their PRD.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mb-10">
            <button data-testid="hero-cta-button" onClick={onStart}
              className="inline-flex items-center justify-center px-8 py-4 text-sm font-semibold text-white bg-zinc-950 rounded-full hover:bg-zinc-800 transition-colors shadow-sm">
              Start Your Project <ArrowRight size={16} className="ml-2" />
            </button>
            <a href="#how-it-works" className="inline-flex items-center justify-center px-8 py-4 text-sm font-semibold text-zinc-900 bg-white border border-zinc-200 rounded-full hover:bg-zinc-50 transition-colors shadow-sm">
              See How It Works
            </a>
          </div>
          <div className="flex items-center gap-4 text-sm text-zinc-500">
            <div className="flex -space-x-2">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="w-7 h-7 rounded-full bg-gradient-to-br from-zinc-300 to-zinc-400 border-2 border-white flex items-center justify-center text-[10px] font-bold text-white">{i}</div>
              ))}
            </div>
            <span><strong className="text-zinc-900">200+</strong> MVPs delivered</span>
            <div className="flex items-center gap-0.5">{[1,2,3,4,5].map(i => <Star key={i} size={13} fill="#18181B" className="text-zinc-900" />)}</div>
          </div>
        </div>
        <div className="relative hidden lg:block">
          <div className="absolute inset-0 bg-gradient-to-br from-zinc-100 to-transparent rounded-3xl -z-10" />
          <picture>
            <source srcSet="/brand/hero.avif" type="image/avif" />
            <source srcSet="/brand/hero.webp" type="image/webp" />
            <img
              src={HERO_IMAGE}
              alt="AI MVP development — AI researches, AI builds, senior developers validate."
              width="1400"
              height="933"
              fetchPriority="high"
              loading="eager"
              decoding="async"
              className="w-full h-auto rounded-2xl shadow-lg border border-zinc-100"
              data-testid="hero-image"
            />
          </picture>
        </div>
      </div>
    </div>
  </section>
);

const TrustBar = () => (
  <div className="bg-zinc-950 py-4">
    <div className="max-w-7xl mx-auto px-4">
      <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
        {[
          { icon: Shield, text: "GDPR Compliant" },
          { icon: CheckCircle, text: "NDA Protected" },
          { icon: CheckCircle, text: "Client Owns IP" },
          { icon: Shield, text: "Stripe Secured" },
          { icon: UserCheck, text: "Senior Dev Reviewed" },
          { icon: Clock, text: "1–4 Week Delivery" },
        ].map(({ icon: Icon, text }) => (
          <div key={text} className="flex items-center gap-2 text-zinc-300 text-sm font-medium">
            <Icon size={14} className="text-zinc-400" />{text}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const UrgencyStrip = () => (
  <section className="bg-amber-50 border-y border-amber-200/70" data-testid="urgency-strip">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8">
        {[
          { icon: Flame, kicker: "Speed is your moat", line: "Launch before competitors finish their pitch deck." },
          { icon: AlertTriangle, kicker: "$100K dev teams are a gamble", line: "Test your idea with a real product first — for ~$6K." },
          { icon: Target, kicker: "Most startups fail building too slow", line: "Founders who ship in weeks beat founders who plan for months." },
        ].map(({ icon: Icon, kicker, line }) => (
          <div key={kicker} className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-900 text-amber-50 flex items-center justify-center flex-shrink-0">
              <Icon size={16} />
            </div>
            <div>
              <div className="text-sm font-bold text-amber-950 leading-snug">{kicker}</div>
              <div className="text-xs text-amber-900/80 leading-relaxed mt-0.5">{line}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WhyDifferent = () => (
  <section className="py-20 md:py-28 bg-white border-t border-zinc-100" data-testid="why-different-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mb-14">
        <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">The difference</span>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-950 leading-tight">
          Why we're different from <span className="line-through decoration-red-500 decoration-2">AI dev shops</span> <span className="text-zinc-500">vibe-coded prototypes</span>.
        </h2>
        <p className="mt-4 text-zinc-600 text-sm leading-relaxed">
          Anyone can ship "AI-generated code." Almost nobody ships AI-generated code that survives a real user, a security review, or a Series A diligence call. That's the line we draw.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {[
          {
            icon: UserCheck,
            tag: "AI generates",
            arrow: "we validate",
            title: "Every line is reviewed by a senior developer.",
            desc: "AI ships fast — and ships subtle bugs faster. A 10-year-experienced engineer reads every component, fixes the brittle parts, and signs off before you ever see it.",
          },
          {
            icon: Layers,
            tag: "MVP scope",
            arrow: "production-grade architecture",
            title: "Built like real software, not a demo.",
            desc: "Proper auth, role-based access, encrypted secrets, GDPR-ready data handling, structured database, observability hooks. Your dev partner inherits a real codebase — not a Frankenstein you'll have to throw away.",
          },
          {
            icon: Zap,
            tag: "Maximum speed",
            arrow: "zero technical-debt explosion",
            title: "Fast doesn't mean disposable.",
            desc: "We refuse projects that would force shortcuts on architecture or security — even when it costs us a sale. The result: an MVP you can scale, hand off, or sell. Not one that collapses the moment you get traction.",
          },
        ].map(({ icon: Icon, tag, arrow, title, desc }) => (
          <div key={tag} className="bg-zinc-50 border border-zinc-200 rounded-2xl p-7 flex flex-col gap-4 hover:border-zinc-300 hover:-translate-y-0.5 transition-all duration-200" data-testid={`why-different-${tag.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="w-10 h-10 rounded-xl bg-zinc-950 text-white flex items-center justify-center flex-shrink-0">
              <Icon size={18} />
            </div>
            <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider w-fit">
              <span className="text-zinc-500">{tag}</span>
              <ArrowRight size={11} className="text-zinc-400" />
              <span className="text-emerald-700">{arrow}</span>
            </div>
            <h3 className="text-lg font-bold text-zinc-950 leading-snug">{title}</h3>
            <p className="text-sm text-zinc-600 leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
      {/* Comparison table — what you get vs typical AI dev shop */}
      <div className="mt-10 bg-gradient-to-br from-zinc-950 to-zinc-900 rounded-2xl p-7 sm:p-9">
        <div className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-5">A side-by-side, no-bullshit version</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2.5">
            <div className="text-sm font-bold text-red-300 mb-3 flex items-center gap-2"><X size={14} /> Typical AI dev shop</div>
            {[
              "Ships whatever the AI spits out",
              "No senior review, no architecture pass",
              "\"It works on my machine\" prototypes",
              "Throwaway code your real dev team rewrites",
              "Vague pricing, hourly billing, scope creep",
            ].map(item => (
              <div key={item} className="flex items-start gap-2 text-sm text-zinc-400">
                <X size={14} className="text-red-400/60 mt-0.5 flex-shrink-0" />{item}
              </div>
            ))}
          </div>
          <div className="space-y-2.5">
            <div className="text-sm font-bold text-emerald-300 mb-3 flex items-center gap-2"><CheckCircle size={14} /> FastPrototype.ai</div>
            {[
              "Senior developer reviews every component",
              "Production-grade auth, DB, secrets, GDPR",
              "Live, deployed, real users from day one",
              "Codebase your future team can scale, not scrap",
              "Fixed price quoted upfront. 50/50 payments. No surprises.",
            ].map(item => (
              <div key={item} className="flex items-start gap-2 text-sm text-zinc-100">
                <CheckCircle size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />{item}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const HowItWorks = () => (
  <section id="how-it-works" className="py-20 md:py-32 bg-zinc-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">The Process</span>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900">How FastPrototype.ai Works</h2>
        <p className="mt-4 text-zinc-500 max-w-xl mx-auto text-sm leading-relaxed">
          We combine cutting-edge AI with experienced human oversight — not a replacement for great software, but the fastest and most cost-effective path to market validation.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
        {[
          {
            step: "01", icon: Lightbulb, title: "Describe Your Vision",
            desc: "Complete our 3-minute project form. Tell us your idea, industry, features, and desired look & feel. Our AI instantly analyses your submission.",
            sub: "Under 3 minutes"
          },
          {
            step: "02", icon: Code2, title: "AI Builds. Senior Devs Review.",
            desc: "Our network of AI agents generates your MVP code. Then a team of Senior Developers (10+ years experience) reviews every component for quality, security and reliability. You get the best of both worlds — speed and expertise.",
            sub: "AI speed + human quality",
            highlight: true
          },
          {
            step: "03", icon: Package, title: "Launch, Validate & Grow",
            desc: "Receive your live, deployed MVP along with full documentation. We also provide a vetted development partner recommendation to scale your product — and can facilitate a smooth handover when you're ready.",
            sub: "Complete handover included"
          },
        ].map(({ step, icon: Icon, title, desc, sub, highlight }) => (
          <div key={step} data-testid={`how-it-works-step-${step}`}
            className={`rounded-2xl p-8 border transition-all duration-200 hover:-translate-y-1 hover:shadow-md ${highlight ? "bg-zinc-950 border-zinc-950" : "bg-white border-zinc-200 shadow-sm"}`}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-5 ${highlight ? "bg-white/10" : "bg-zinc-100"}`}>
              <Icon size={20} className={highlight ? "text-white" : "text-zinc-700"} />
            </div>
            <div className={`text-xs font-bold uppercase tracking-widest mb-2 ${highlight ? "text-zinc-400" : "text-zinc-400"}`}>{step}</div>
            <h3 className={`text-xl font-semibold mb-3 ${highlight ? "text-white" : "text-zinc-900"}`}>{title}</h3>
            <p className={`text-sm leading-relaxed mb-4 ${highlight ? "text-zinc-300" : "text-zinc-600"}`}>{desc}</p>
            <div className={`text-xs font-semibold px-3 py-1.5 rounded-full w-fit ${highlight ? "bg-white/10 text-zinc-200" : "bg-zinc-100 text-zinc-600"}`}>{sub}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-zinc-200 rounded-2xl p-6 text-center">
        <p className="text-sm text-zinc-600 max-w-2xl mx-auto">
          <strong className="text-zinc-900">Important:</strong> FastPrototype.ai is not a full software development agency. We specialise exclusively in rapid MVP delivery to help you test your market, validate demand, and prove your concept — before committing to a full product build. Think of us as your fastest path from idea to live product.
        </p>
      </div>
    </div>
  </section>
);

const Deliverables = () => (
  <section className="py-20 md:py-32 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        <div>
          <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">What You Receive</span>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900 mb-6">Everything You Need to Launch and Grow</h2>
          <p className="text-zinc-600 text-sm leading-relaxed mb-8">
            Every FastPrototype.ai project comes with a complete package — not just code, but everything you need to operate, understand, and eventually scale your product.
          </p>
          <div className="space-y-4">
            {[
              { icon: Zap, title: "Working MVP", desc: "Deployed, live, and accessible to real users from day one." },
              { icon: FileText, title: "Database Documentation", desc: "Full schema documentation, entity relationships, and data model guide." },
              { icon: Code2, title: "Software Architecture Guide", desc: "Technical overview of your codebase, API structure, and deployment setup." },
              { icon: Users, title: "Recommended Development Partner", desc: "A vetted full-stack development partner to scale your product when you're ready." },
              { icon: Handshake, title: "Facilitated Handover (Optional)", desc: "We can facilitate a smooth transition to your development partner — documentation, walkthrough, and onboarding support." },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-4">
                <div className="w-9 h-9 bg-zinc-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon size={16} className="text-zinc-700" />
                </div>
                <div>
                  <div className="font-semibold text-zinc-900 text-sm">{title}</div>
                  <div className="text-zinc-500 text-xs mt-0.5">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-6">
            <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4">Your Launch Timeline</div>
            {[
              { day: "Day 1", label: "Project confirmed + development starts", done: true },
              { day: "Day 2–5", label: "AI agents build your MVP", done: true },
              { day: "Day 3–7", label: "Senior Dev review & quality assurance", done: true },
              { day: "Week 1–4", label: "Your MVP is deployed live", done: false },
              { day: "Post-launch", label: "30-day warranty + partner handover", done: false },
            ].map(({ day, label, done }) => (
              <div key={day} className="flex items-start gap-3 py-3 border-b border-zinc-100 last:border-0">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${done ? "bg-green-600" : "bg-zinc-200"}`}>
                  {done && <CheckCircle size={11} className="text-white" />}
                </div>
                <div>
                  <div className="text-xs font-bold text-zinc-500">{day}</div>
                  <div className="text-sm text-zinc-700">{label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const WhyValidate = () => (
  <section className="py-20 md:py-32 bg-zinc-950" data-testid="why-validate-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-14">
        <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">The Smart Founder Strategy</span>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-white">Why Validate Before You Build?</h2>
        <p className="mt-4 text-zinc-400 max-w-xl mx-auto text-sm leading-relaxed">
          The biggest risk in software is spending six figures building something nobody wants. FastPrototype.ai lets you test the market for a fraction of the cost — and that changes everything.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        {[
          {
            icon: TrendingUp, title: "Investor-Ready in Weeks",
            desc: "Investors fund traction, not ideas. A live MVP with real users is the most powerful pitch tool you can bring to a seed round — far more compelling than slides alone.",
            stat: "3–5×", statLabel: "higher investor conversion with live demo"
          },
          {
            icon: BarChart3, title: "Real Market Validation",
            desc: "Stop guessing what users want. Launch, measure, and learn. Real usage data from your MVP gives you the insights to build the right product — and attract the right investors.",
            stat: "90%", statLabel: "of startups fail from lack of market validation"
          },
          {
            icon: Shield, title: "Manage Your Risk",
            desc: "A full custom build can cost $100K–$500K and take 12+ months. Our MVPs start at ~$6,000 and deliver in weeks. Test your hypothesis before making a major financial commitment.",
            stat: "~$6K", statLabel: "vs $100K+ for a full build"
          },
        ].map(({ icon: Icon, title, desc, stat, statLabel }) => (
          <div key={title} className="bg-white/5 border border-white/10 rounded-2xl p-7 hover:bg-white/10 transition-colors">
            <Icon size={24} className="text-white mb-5" />
            <div className="text-3xl font-extrabold text-white mb-0.5">{stat}</div>
            <div className="text-xs text-zinc-400 mb-4">{statLabel}</div>
            <h3 className="font-semibold text-white mb-2 text-base">{title}</h3>
            <p className="text-zinc-400 text-sm leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
      <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
        <div className="text-lg font-semibold text-white mb-2">"The best founders we back don't just have great ideas — they have proof."</div>
        <div className="text-zinc-400 text-sm">— Common feedback from Seed & Series A investors</div>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-sm text-zinc-400">
          {["Proof of concept", "User traction data", "Technical validation", "Market demand evidence"].map(t => (
            <div key={t} className="flex items-center gap-2"><CheckCircle size={13} className="text-green-500" />{t}</div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Portfolio = () => (
  <section className="py-20 md:py-32 bg-zinc-50" data-testid="portfolio-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-14">
        <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">Case Studies</span>
        <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">MVPs We've Launched</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          {
            name: "AviShare", industry: "Aviation", desc: "Aircraft cost-sharing platform connecting pilots and plane owners. Booking, cost splitting, and flight logging.",
            tech: ["React", "FastAPI", "Stripe"], weeks: 3, price: "$6,200", outcome: "500+ pilots onboarded in month 1"
          },
          {
            name: "MedTrack", industry: "Healthcare", desc: "Patient dashboard for remote monitoring. Secure data entry, clinician review portal, and automated alerts.",
            tech: ["React", "MongoDB", "Auth"], weeks: 3, price: "$8,400", outcome: "Pilot launched with 3 NHS clinics"
          },
          {
            name: "MarketBase", industry: "Marketplace", desc: "B2B supplier discovery platform with verified listings, RFQ system, and messaging between buyers and sellers.",
            tech: ["React", "FastAPI", "Payments"], weeks: 4, price: "$13,000", outcome: "Used in seed round pitch — funded"
          },
          {
            name: "FinFlow", industry: "Finance", desc: "Budget analytics tool for SMEs. Bank feed integration, category tagging, and monthly insights reports.",
            tech: ["React", "MongoDB", "API"], weeks: 2, price: "$10,100", outcome: "100 SME signups in first 2 weeks"
          },
        ].map(({ name, industry, desc, tech, weeks, price, outcome }) => (
          <div key={name} data-testid={`portfolio-${name.toLowerCase()}`}
            className="bg-white border border-zinc-200 rounded-2xl p-6 flex flex-col hover:shadow-md hover:-translate-y-1 transition-all duration-200">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-extrabold text-zinc-950">{name}</div>
              <span className="text-xs font-semibold px-2.5 py-0.5 bg-zinc-100 text-zinc-600 rounded-full border border-zinc-200">{industry}</span>
            </div>
            <p className="text-xs text-zinc-600 leading-relaxed mb-4 flex-1">{desc}</p>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {tech.map(t => <span key={t} className="text-[10px] font-medium px-2 py-0.5 bg-zinc-100 text-zinc-500 rounded-full">{t}</span>)}
            </div>
            <div className="border-t border-zinc-100 pt-4 space-y-1.5">
              <div className="flex justify-between text-xs"><span className="text-zinc-500">Delivered in</span><span className="font-semibold text-zinc-800">{weeks} weeks</span></div>
              <div className="flex justify-between text-xs"><span className="text-zinc-500">MVP price</span><span className="font-bold text-zinc-950">{price}</span></div>
              <div className="mt-2 text-xs text-green-700 font-medium flex items-center gap-1.5">
                <TrendingUp size={11} />{outcome}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PricingSection = () => (
  <section className="py-20 md:py-32 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">Transparent Pricing</span>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900 mb-6">AI-Driven. Fixed Price. No Surprises.</h2>
          <div className="space-y-4">
            {[
              "AI analyses your idea and scores its technical complexity",
              "Fixed quote generated instantly — before any payment",
              "50% upfront to start development",
              "50% due only upon successful delivery",
              "No hourly billing, no scope creep charges",
            ].map(item => (
              <div key={item} className="flex items-start gap-3">
                <CheckCircle size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-zinc-700 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-8">
          <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4">Typical MVP Pricing</div>
          <div className="text-5xl font-extrabold text-zinc-950 mb-2">~$6,000</div>
          <div className="text-zinc-500 text-sm mb-6">USD · Fixed quote before payment</div>
          <div className="space-y-3 text-sm">
            {[
              { label: "Complexity analysis", value: "AI-powered" },
              { label: "Scope of work", value: "Full document" },
              { label: "Payment terms", value: "50% / 50%" },
              { label: "Delivery", value: "1–4 weeks" },
              { label: "Documentation", value: "Included" },
              { label: "Dev partner referral", value: "Included" },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between py-2 border-b border-zinc-200 last:border-0">
                <span className="text-zinc-600">{label}</span>
                <span className="font-semibold text-zinc-900">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const DeliveryOptions = () => (
  <section className="py-20 md:py-32 bg-zinc-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">Build Packages</span>
        <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Pick the package that fits your launch</h2>
        <p className="mt-3 text-zinc-500 text-sm">Each package is a complete product. More time means more human review, documentation, and consulting.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            weeks: 1, name: "Speed Build", badge: "Fastest",
            features: ["Working AI-generated software", "2 hours of iterations"],
          },
          {
            weeks: 2, name: "Reviewed Build", badge: "Most popular", highlight: true,
            features: ["Everything in Speed Build", "Senior developer review", "Bug fixing pass", "6 hours of iterations & consulting"],
          },
          {
            weeks: 3, name: "Architected Build", badge: "Production-ready",
            features: ["Everything in Reviewed Build", "Documented database data model", "Data relationships diagram"],
          },
          {
            weeks: 4, name: "Premium MVP", badge: "Premium",
            features: ["Everything in Architected Build", "Ready for limited production release", "12 hours of iterations & consulting", "Full documentation of every feature"],
          },
        ].map(({ weeks, name, badge, features, highlight }) => (
          <div key={weeks} data-testid={`delivery-option-${weeks}wk`}
            className={`rounded-2xl p-6 border text-left transition-all duration-200 hover:-translate-y-1 flex flex-col ${highlight ? "bg-zinc-950 border-zinc-950 text-white" : "bg-white border-zinc-200"}`}>
            <div className={`text-[10px] font-bold uppercase tracking-widest mb-2 ${highlight ? "text-emerald-300" : "text-emerald-700"}`}>{badge}</div>
            <div className={`text-xs font-bold uppercase tracking-wider mb-1 ${highlight ? "text-zinc-400" : "text-zinc-500"}`}>{weeks} {weeks === 1 ? "week" : "weeks"}</div>
            <div className={`text-xl font-extrabold mb-3 ${highlight ? "text-white" : "text-zinc-950"}`}>{name}</div>
            <ul className="space-y-2 flex-1">
              {features.map((f) => (
                <li key={f} className={`flex items-start gap-2 text-[13px] leading-snug ${highlight ? "text-zinc-200" : "text-zinc-700"}`}>
                  <CheckCircle size={12} className={`mt-1 flex-shrink-0 ${highlight ? "text-emerald-300" : "text-emerald-600"}`} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <p className="mt-8 text-center text-xs text-zinc-500">Exact price for your project is calculated and locked once you submit your idea.</p>
    </div>
  </section>
);

const Testimonials = () => (
  <section className="py-20 md:py-32 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">Client Stories</span>
        <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Founders Who Launched Fast</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {[
          { name: "Sarah Chen", role: "Founder, MedTrack", quote: "FastPrototype.ai delivered our healthcare dashboard MVP in 3 weeks. The AI scoping was incredibly accurate — and the Senior Dev review gave us confidence in the code quality. The final price matched the quote exactly.", img: "https://images.unsplash.com/photo-1758691737644-ef8be18256c3?w=80&h=80&fit=crop&crop=face" },
          { name: "Marcus Rivera", role: "CEO, AviShare", quote: "We described our aircraft cost-sharing app and got a $6,200 quote in minutes. We took the MVP to investors within 4 weeks of launch — the live demo was worth more than any pitch deck. Raised our seed round.", img: "https://images.unsplash.com/photo-1758691737587-7630b4d31d16?w=80&h=80&fit=crop&crop=face" },
        ].map(({ name, role, quote, img }) => (
          <div key={name} className="bg-zinc-50 border border-zinc-200 rounded-2xl p-8">
            <div className="flex items-center gap-0.5 mb-4">{[1,2,3,4,5].map(i => <Star key={i} size={14} fill="#18181B" className="text-zinc-900" />)}</div>
            <p className="text-zinc-700 leading-relaxed mb-6 text-sm">"{quote}"</p>
            <div className="flex items-center gap-3">
              <img src={img} alt={name} className="w-10 h-10 rounded-full object-cover" />
              <div><div className="font-semibold text-zinc-900 text-sm">{name}</div><div className="text-zinc-500 text-xs">{role}</div></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FinalCTA = ({ onStart }) => (
  <section className="py-20 md:py-32 bg-zinc-950">
    <div className="max-w-3xl mx-auto px-4 text-center">
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">Ready to Test Your Market?</h2>
      <p className="text-zinc-400 text-base sm:text-lg mb-2">Describe your idea and get a full scope, documentation plan, and fixed price in minutes.</p>
      <p className="text-zinc-500 text-sm mb-8">No commitment. No credit card. Just your idea.</p>
      <button data-testid="final-cta-button" onClick={onStart}
        className="inline-flex items-center px-8 py-4 text-sm font-semibold bg-white text-zinc-950 rounded-full hover:bg-zinc-100 transition-colors shadow-sm">
        Start Your Project Now <ArrowRight size={16} className="ml-2" />
      </button>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-sm text-zinc-500">
        <span className="flex items-center gap-1.5"><Shield size={13} />GDPR Compliant</span>
        <span className="flex items-center gap-1.5"><CheckCircle size={13} />NDA Included</span>
        <span className="flex items-center gap-1.5"><CheckCircle size={13} />You Own the IP</span>
        <span className="flex items-center gap-1.5"><UserCheck size={13} />Senior Dev Reviewed</span>
      </div>
    </div>
  </section>
);

export default function Landing() {
  const navigate = useNavigate();
  const onStart = () => navigate("/build");
  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <CookieConsentInline />
      <Navbar />
      <main>
        <HeroSection onStart={onStart} />
        <TrustBar />
        <UrgencyStrip />
        <HowItWorks />
        <WhyDifferent />
        <Deliverables />
        <WhyValidate />
        <PricingSection />
        <DeliveryOptions />
        <Portfolio />
        <Testimonials />
        <FinalCTA onStart={onStart} />
      </main>
      <Footer />
    </div>
  );
}
